import { useCallback, useRef, type ReactElement } from "react";
import type { ContextMenuItem } from "@/components/common/ContextMenu";
import { uploadFilesToActiveEnvironment } from "@/lib/environment-files";
import {
	selectEffectiveEnvironmentId,
	selectEnvironmentUploadOperation,
	useChatStore,
} from "@/stores/useChatStore";

interface EnvironmentUpload {
	/** ファイル選択用・フォルダ選択用の隠し input。利用側で必ず描画する。 */
	uploadInputs: ReactElement;
	/** コンテキストメニューへ差し込むアップロード項目 (ファイル / フォルダ)。 */
	uploadMenuItems: ContextMenuItem[];
}

interface EnvironmentUploadOptions {
	disabled?: boolean;
	/** 選んだファイルの置き方。既定は ~ の dirPath。 */
	upload?: (files: File[]) => Promise<void>;
}

/**
 * 環境ファイルのアップロード UI を提供する。隠し input とコンテキストメニュー項目を返し、
 * メニュー項目を選ぶと対応する選択ダイアログ (ファイル単体 / フォルダ丸ごと) が開いて
 * アップロードする。<paramref name="dirPath"/> はアップロード先の相対ディレクトリ
 * (ルート直下は空文字)。<paramref name="testIdPrefix"/> は input・メニュー項目の
 * data-testid 接頭辞。
 */
export function useEnvironmentUpload(
	dirPath: string,
	testIdPrefix: string,
	options: EnvironmentUploadOptions = {},
): EnvironmentUpload {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const folderInputRef = useRef<HTMLInputElement>(null);
	const effectiveEnvironmentId = useChatStore(selectEffectiveEnvironmentId);
	const activeOperation = useChatStore((state) =>
		selectEnvironmentUploadOperation(state, effectiveEnvironmentId),
	);
	const disabled = (options.disabled ?? false) || activeOperation !== null;

	const handleUploadChange = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>) => {
			const files = Array.from(e.target.files ?? []);
			// 同名ファイルの再選択でも change が発火するよう、取得後すぐに値をリセットする。
			e.target.value = "";
			if (files.length === 0 || (options.disabled ?? false)) return;
			await (options.upload ?? ((picked) => uploadFilesToActiveEnvironment(dirPath, picked)))(
				files,
			);
		},
		[dirPath, options.disabled, options.upload],
	);

	const uploadInputs = (
		<>
			<input
				ref={fileInputRef}
				type="file"
				multiple
				className="hidden"
				onChange={handleUploadChange}
				data-testid={`${testIdPrefix}-files-input`}
			/>
			<input
				ref={folderInputRef}
				type="file"
				multiple
				webkitdirectory=""
				className="hidden"
				onChange={handleUploadChange}
				data-testid={`${testIdPrefix}-folder-input`}
			/>
		</>
	);

	const uploadMenuItems: ContextMenuItem[] = [
		{
			label: "ファイルをアップロード",
			testId: `${testIdPrefix}-menu-files`,
			disabled,
			onSelect: () => fileInputRef.current?.click(),
		},
		{
			label: "フォルダをアップロード",
			testId: `${testIdPrefix}-menu-folder`,
			disabled,
			onSelect: () => folderInputRef.current?.click(),
		},
	];

	return { uploadInputs, uploadMenuItems };
}
